import { useCallback, useMemo } from 'react';

import { matchPath } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { IRM_PERMISSION_ID, WEBCAST_PERMISSION_ID } from '@/config/environment';
import {
  modulesPriorityByView,
  permissionKeys,
  freeRoutes,
  permissions,
} from '@/constants/permissions';
import { MyCompaniesResponse } from '@/features/login/api/getMyCompanies';
import { queryClient } from '@/lib/react-query';
import { useViewStore } from '@/stores/useViewStore';

const profileIrmConfigRoute = `/profile/irm-config`;
const irmConfigRoute = `/irm/config`;

export const usePermissions = () => {
  const { view, companyId } = useViewStore(
    (state) => ({ view: state.view, companyId: state.company }),
    shallow,
  );

  const getCurrentCompany = useCallback(() => {
    const queryData = queryClient.getQueryData<{ data: MyCompaniesResponse }>([`my-companies`]);

    if (queryData) {
      const currentCompany = queryData.data.data.find((item) => item.id === companyId);

      return currentCompany;
    }

    return undefined;
  }, [companyId]);

  const userPermissions = useMemo(() => {
    const currentCompany = getCurrentCompany();

    if (currentCompany) {
      return currentCompany?.userCompanyRoles?.at(0)?.role.permissions.map((p) => p.id);
    }

    return undefined;
  }, [getCurrentCompany]);

  const canAccess = useCallback(
    (module: keyof typeof permissions) => {
      if (module === permissions[`*`]) return true;

      const currentCompany = getCurrentCompany();

      if (currentCompany) {
        const companyTool = currentCompany.tool[module as keyof typeof currentCompany.tool];

        if (!companyTool && view === `company`) return false;

        return (
          currentCompany?.userCompanyRoles
            ?.at(0)
            ?.role.permissions.some((item) => item.id === permissions[module].id) ?? false
        );
      }

      return false;
    },
    [getCurrentCompany, view],
  );

  const startingPage = useMemo(() => {
    const currentCompany = getCurrentCompany();

    const moduleId = modulesPriorityByView[view].find((module) => {
      const key = permissionKeys[module];

      if (view === `company`) {
        return (
          currentCompany?.tool[key as keyof typeof currentCompany.tool] &&
          userPermissions?.find((userPermission) => userPermission === module)
        );
      }

      return userPermissions?.find((userPermission) => userPermission === module);
    });

    // The AI module shares the IRM permission id, so the priority list (by id) cannot rank it.
    // It comes right after IRM and Webcast, ahead of every other module.
    const opensIrmOrWebcast = moduleId === IRM_PERMISSION_ID || moduleId === WEBCAST_PERMISSION_ID;

    if (view === `company` && !opensIrmOrWebcast && canAccess(`ai`)) {
      return permissions.ai.startingPage;
    }

    if (moduleId) {
      const key = permissionKeys[moduleId];
      const permission = permissions[key];

      return permission.startingPage;
    }

    return undefined;
  }, [userPermissions, view, getCurrentCompany, canAccess]);

  const hasRoute = useCallback(
    (requestedRoute: string) => {
      // The IRM settings of the new layout live under the profile, but they are governed by the
      // IRM module, exactly like `/irm/config/*`.
      const route = requestedRoute.replace(profileIrmConfigRoute, irmConfigRoute);

      if (freeRoutes.includes(route)) return true;
      const currentCompany = getCurrentCompany();

      // Demais Serviços is open to everyone in the company view, whatever the profile holds. The
      // legacy `/demais-servicos/*` only redirects to its route under `/irm/*`.
      if (view === `company` && route.startsWith('/demais-servicos')) return true;
      if (view === `company` && matchPath(permissions.services.route, route)) return true;
      if (view === `company` && route.startsWith('/automation')) return true;
      if (freeRoutes.includes(route)) return true;
      // `/webcast/*` and `/inteligencia-artificial/*` only redirect to their routes under `/irm/*`,
      // which are checked below.
      if (route.startsWith(`/webcast`) || route.startsWith(`/inteligencia-artificial`)) return true;

      if (currentCompany) {
        // The videoconference and Astrid live under `/irm/*` but are governed by their own modules,
        // so they must be decided before the generic per-permission check below. Both were closed
        // in the `prisma` view before they moved, so they stay closed there.
        if (matchPath(permissions.webcast.route, route)) {
          return view === `company` && canAccess(`webcast`);
        }
        if (matchPath(permissions.ai.route, route)) return view === `company` && canAccess(`ai`);

        return (
          currentCompany?.userCompanyRoles?.at(0)?.role.permissions.some((item) => {
            const key = permissionKeys[item.id];
            const permission = permissions[key];
            const companyTool = currentCompany.tool[key as keyof typeof currentCompany.tool];

            if (!companyTool && view === `company`) return false;

            return (
              matchPath(permission.route, route) &&
              modulesPriorityByView[view].includes(permission.id)
            );
          }) ?? false
        );
      }

      return true;
    },
    [getCurrentCompany, view, canAccess],
  );

  return { userPermissions, startingPage, canAccess, hasRoute };
};
